import React, { useState, useEffect } from 'react';
import { X, User, Lock, CreditCard, Bell, Crown, Shield, Wallet, Globe, Link as LinkIcon } from 'lucide-react';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { HelpSystem } from '../../components/tour';
import ProfileSection from '../account/ProfileSection';
import PasswordSection from '../account/PasswordSection';
import PlanSection from '../account/PlanSection';
import CreditsSection from '../account/CreditsSection';
import BillingSection from '../account/BillingSection';
import NotificationsSection from '../account/NotificationsSection';
import SecuritySection from '../account/SecuritySection';
import DomainsSection from '../account/DomainsSection';
import IntegrationsSection from '../account/IntegrationsSection';

// Define sections outside of component for export access
const ACCOUNT_SECTIONS = [
  { id: 'profile', label: 'Profile', icon: User, component: ProfileSection },
  { id: 'password', label: 'Password', icon: Lock, component: PasswordSection },
  { id: 'plan', label: 'Plan', icon: Crown, component: PlanSection },
  { id: 'credits', label: 'Credits', icon: Wallet, component: CreditsSection },
  { id: 'billing', label: 'Billing', icon: CreditCard, component: BillingSection },
  { id: 'domains', label: 'Domains', icon: Globe, component: DomainsSection },
  { id: 'notifications', label: 'Notifications', icon: Bell, component: NotificationsSection },
  { id: 'security', label: 'Security', icon: Shield, component: SecuritySection },
  { id: 'integrations', label: 'Integrations', icon: LinkIcon, component: IntegrationsSection },
];

// Export sections IDs for validation
export const getAccountSectionIds = (): string[] => 
  ACCOUNT_SECTIONS.map(section => section.id);

const AccountSettingsModal: React.FC = () => {
  const { isAccountSettingsOpen, closeAccountSettings, initialSection } = useSettingsModal();
  const [currentSection, setCurrentSection] = useState<string>('profile');
  
  // Set initial section if provided by context (for payment redirect)
  useEffect(() => {
    if (initialSection && isAccountSettingsOpen) {
      setCurrentSection(initialSection);
    }
  }, [initialSection, isAccountSettingsOpen]);

  // No need to filter sections - they will show upgrade prompts if needed
  const sections = ACCOUNT_SECTIONS;

  // Ensure current section is valid when sections change (e.g., feature flag gating)
  useEffect(() => {
    if (!sections.find((s) => s.id === currentSection)) {
      setCurrentSection(sections[0]?.id || 'profile');
    }
  }, [sections, currentSection]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isAccountSettingsOpen) {
        closeAccountSettings();
      }
    };

    if (isAccountSettingsOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isAccountSettingsOpen, closeAccountSettings]);

  const handleSectionChange = (sectionId: string) => {
    setCurrentSection(sectionId);
  };

  const currentSectionData = sections.find(section => section.id === currentSection);
  const CurrentSectionComponent = currentSectionData?.component || ProfileSection;

  if (!isAccountSettingsOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={closeAccountSettings}
    >
      <div 
        className="bg-white dark:bg-neutral-900 rounded-xl w-full h-full lg:w-[1200px] lg:h-[800px] max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col shadow-2xl border border-neutral-200 dark:border-neutral-700 ring-1 ring-black/5 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/30">
          <div className="flex items-center">
            <User className="w-6 h-6 text-neutral-600 dark:text-neutral-400 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Account Settings</h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">Manage your profile, password, and subscription preferences</p>
            </div>
          </div>
          <button
            onClick={closeAccountSettings}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <X size={24} className="text-neutral-500 dark:text-neutral-400" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto lg:overflow-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-track]:dark:bg-neutral-800 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
          <div className="flex flex-col lg:flex-row min-h-full lg:h-full gap-4 lg:gap-0 p-4 lg:p-0">
            {/* Sidebar Navigation */}
            <div className="lg:w-64 flex-shrink-0 lg:border-r border-b lg:border-b-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg lg:rounded-none lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
              <nav className="p-4">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = section.id === currentSection;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleSectionChange(section.id)}
                      data-tour={`account-${section.id}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mb-2 transition-all duration-200 ${
                        isActive
                          ? 'bg-brand-primary text-black'
                          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                      }`}
                    >
                      <Icon size={18} />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 bg-white dark:bg-neutral-900 lg:bg-white lg:dark:bg-neutral-900 lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-neutral-100 lg:[&::-webkit-scrollbar-track]:dark:bg-neutral-800 lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
              <div className="bg-white dark:bg-neutral-800 rounded-lg lg:rounded-none lg:bg-white lg:dark:bg-neutral-900 p-4 lg:p-6 h-full">
                <CurrentSectionComponent />
              </div>
            </div>
          </div>
        </div>
      </div>
      <HelpSystem pageName="account" />
    </div>
  );
};

export default AccountSettingsModal;
